<template>
    <div>
        <div class="row q-col-gutter-sm q-mb-xs">
            <div class="col-6">
                <p class="q-mb-xs">
                    <strong>First Name <sup v-if="requireName" class="text-negative">*</sup></strong>
                </p>
                <q-input
                    :model-value="draft.first_name"
                    outlined :dense="dense" maxlength="255"
                    :placeholder="requireName ? 'First name' : 'First name (leave blank for Anonymous)'"
                    hide-bottom-space
                    :rules="requireName ? [v => !!String(v ?? '').trim() || 'First name is required.'] : []"
                    @update:model-value="value => updateField('first_name', value)"
                />
            </div>
            <div class="col-6">
                <p class="q-mb-xs"><strong>Last Name</strong></p>
                <q-input
                    :model-value="draft.last_name"
                    outlined :dense="dense" maxlength="255"
                    placeholder="Last name"
                    @update:model-value="value => updateField('last_name', value)"
                />
            </div>
        </div>
        <div class="row q-col-gutter-sm">
            <div class="col-12">
                <p class="q-mb-xs"><strong>Preferred Name</strong></p>
                <q-input
                    :model-value="draft.preferred_name"
                    outlined :dense="dense" maxlength="255"
                    placeholder="Preferred name (optional)"
                    @update:model-value="value => updateField('preferred_name', value)"
                />
            </div>
        </div>

        <div class="q-mt-md">
            <div class="row items-center q-mb-xs">
                <p class="q-my-none"><strong>Other Known Names</strong></p>
                <q-space />
                <q-btn
                    flat dense no-caps
                    color="primary"
                    icon="add"
                    label="Add known name"
                    @click="addAlias"
                />
            </div>
            <div class="text-caption text-grey-7 q-mb-sm">
                Add any other names this person may be known by and, if known, where that name came from.
            </div>
            <div
                v-for="(alias, index) in (draft.aliases || [])"
                :key="alias.id || index"
                class="row q-col-gutter-sm items-start q-mb-sm"
            >
                <div class="col-4">
                    <q-input
                        :model-value="alias.first_name"
                        outlined :dense="dense" maxlength="255"
                        label="First name"
                        hide-bottom-space
                        :rules="[() => aliasHasName(alias) || 'A first or last name is required.']"
                        @update:model-value="value => updateAliasField(index, 'first_name', value)"
                    />
                </div>
                <div class="col-4">
                    <q-input
                        :model-value="alias.last_name"
                        outlined :dense="dense" maxlength="255"
                        label="Last name"
                        hide-bottom-space
                        @update:model-value="value => updateAliasField(index, 'last_name', value)"
                    />
                </div>
                <div class="col">
                    <q-select
                        :model-value="alias.source"
                        :options="filteredAliasSourceOptions"
                        outlined :dense="dense" maxlength="255"
                        label="Source (optional)"
                        placeholder="e.g. Driver's License"
                        use-input
                        fill-input
                        hide-selected
                        clearable
                        input-debounce="0"
                        new-value-mode="add-unique"
                        hide-bottom-space
                        @filter="filterAliasSources"
                        @update:model-value="value => updateAliasField(index, 'source', value)"
                    >
                        <template v-slot:no-option>
                            <q-item>
                                <q-item-section class="text-grey-7">
                                    Press Enter to use a custom source
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                </div>
                <div class="col-auto">
                    <q-btn
                        flat round dense
                        color="negative"
                        icon="delete_outline"
                        aria-label="Remove known name"
                        @click="removeAlias(index)"
                    />
                </div>
            </div>
        </div>

        <q-separator class="q-mt-md q-mb-sm" />

        <div class="row q-col-gutter-sm">
            <div class="col-12">
                <p class="q-mb-xs"><strong>Email</strong></p>
                <q-input
                    :model-value="draft.email"
                    outlined :dense="dense" maxlength="255"
                    type="email" inputmode="email"
                    placeholder="Email address"
                    hide-bottom-space
                    :rules="[v => {
                        const value = String(v ?? '').trim();
                        return value === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Please enter a valid email address.';
                    }]"
                    @update:model-value="value => updateField('email', value)"
                />
            </div>
        </div>

        <div class="q-mt-md">
            <div class="row items-center q-mb-xs">
                <p class="q-my-none"><strong>Phone Numbers</strong></p>
                <q-space />
                <q-btn
                    flat dense no-caps
                    color="primary"
                    icon="add"
                    label="Add phone number"
                    @click="addPhoneNumber"
                />
            </div>
            <div v-if="!phoneNumbers.length" class="text-caption text-grey-7 q-mb-sm">
                No phone numbers recorded.
            </div>
            <div
                v-for="(phone, index) in phoneNumbers"
                :key="phone.id || index"
                class="row q-col-gutter-sm items-start q-mb-sm"
            >
                <div class="col-12 col-sm-4">
                    <q-select
                        :model-value="phone.phone_type"
                        :options="filteredPhoneTypeOptions"
                        outlined :dense="dense" maxlength="50"
                        label="Type"
                        placeholder="e.g. Mobile"
                        use-input
                        fill-input
                        hide-selected
                        input-debounce="0"
                        new-value-mode="add-unique"
                        hide-bottom-space
                        :rules="[v => !!String(v ?? '').trim() || 'Phone type is required.']"
                        @filter="filterPhoneTypes"
                        @update:model-value="value => updatePhoneNumberField(index, 'phone_type', value)"
                    >
                        <template v-slot:no-option>
                            <q-item>
                                <q-item-section class="text-grey-7">
                                    Press Enter to use a custom type
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                </div>
                <div class="col">
                    <q-input
                        :model-value="phone.phone_number"
                        outlined :dense="dense" maxlength="50"
                        type="tel" inputmode="tel"
                        label="Phone number"
                        placeholder="Phone number"
                        hide-bottom-space
                        :rules="[v => phoneNumberValidation(v)]"
                        @update:model-value="value => updatePhoneNumberField(index, 'phone_number', value)"
                    />
                </div>
                <div class="col-auto">
                    <q-btn
                        flat round dense
                        color="negative"
                        icon="delete_outline"
                        aria-label="Remove phone number"
                        @click="removePhoneNumber(index)"
                    />
                </div>
            </div>
        </div>

        <div class="row q-mt-sm">
            <q-btn flat dense no-caps color="primary" @click="showMore = !showMore">
                <q-icon :name="showMore ? 'expand_less' : 'expand_more'" class="q-mr-xs" size="18px" />
                {{ showMore ? 'Fewer options' : 'More options' }}
            </q-btn>
        </div>

        <template v-if="showMore">
            <q-separator class="q-mt-sm q-mb-sm" />
            <div class="row q-col-gutter-sm q-mb-xs">
                <div class="col-4">
                    <p class="q-mb-xs"><strong>Date of Birth</strong></p>
                    <q-input
                        :model-value="draft.date_of_birth"
                        outlined :dense="dense"
                        mask="##/##/####"
                        placeholder="DD/MM/YYYY"
                        hide-bottom-space
                        :rules="[v => {
                            const value = String(v ?? '').trim();
                            return value === '' || /^\d{2}\/\d{2}\/\d{4}$/.test(value) || 'Use DD/MM/YYYY.';
                        }]"
                        @update:model-value="value => updateField('date_of_birth', value)"
                    >
                        <template v-slot:append>
                            <q-icon name="event" class="cursor-pointer">
                                <q-popup-proxy transition-show="scale" transition-hide="scale">
                                    <q-date
                                        :model-value="draft.date_of_birth"
                                        mask="DD/MM/YYYY"
                                        @update:model-value="value => updateField('date_of_birth', value)"
                                    />
                                </q-popup-proxy>
                            </q-icon>
                        </template>
                    </q-input>
                </div>
                <div class="col-4">
                    <p class="q-mb-xs"><strong>Gender</strong></p>
                    <q-select
                        :model-value="draft.gender_id"
                        :options="filteredGenderOptions"
                        outlined :dense="dense" clearable
                        use-input input-debounce="0"
                        new-value-mode="add"
                        :placeholder="draft.gender_id ? '' : 'Select or type a gender'"
                        @filter="(val, update) => filterFn('gender', val, update)"
                        @update:model-value="value => updateField('gender_id', value)"
                    />
                </div>
                <div class="col-4">
                    <p class="q-mb-xs"><strong>NHI</strong></p>
                    <q-input
                        :model-value="draft.nhi"
                        outlined :dense="dense" maxlength="50"
                        placeholder="e.g. ABC1234"
                        @update:model-value="value => updateField('nhi', String(value ?? '').toUpperCase())"
                    />
                </div>
            </div>

            <div class="row q-col-gutter-sm">
                <div class="col-6">
                    <p class="q-mb-xs"><strong>Ethnicity</strong></p>
                    <q-select
                        :model-value="draft.ethnicity_id"
                        :options="filteredEthnicityOptions"
                        outlined :dense="dense" clearable
                        use-input input-debounce="0"
                        new-value-mode="add"
                        :placeholder="draft.ethnicity_id ? '' : 'Select or type an ethnicity'"
                        @filter="(val, update) => filterFn('ethnicity', val, update)"
                        @update:model-value="value => updateField('ethnicity_id', value)"
                    />
                </div>
                <div class="col-6">
                    <p class="q-mb-xs"><strong>Iwi</strong></p>
                    <q-select
                        :model-value="draft.iwi_id"
                        :options="filteredIwiOptions"
                        outlined :dense="dense" clearable
                        use-input input-debounce="0"
                        new-value-mode="add"
                        :placeholder="draft.iwi_id ? '' : 'Select or type an iwi'"
                        @filter="(val, update) => filterFn('iwi', val, update)"
                        @update:model-value="value => updateField('iwi_id', value)"
                    />
                </div>
            </div>

            <q-separator class="q-mt-md q-mb-sm" />

            <!-- Address -->
            <div class="row q-col-gutter-sm q-mb-xs">
                <div class="col-12">
                    <p class="q-mb-xs"><strong>Address</strong></p>
                    <q-input
                        :model-value="draft.address_line_1"
                        outlined :dense="dense" maxlength="255"
                        placeholder="Street address"
                        @update:model-value="value => updateField('address_line_1', value)"
                    />
                </div>
            </div>
            <div class="row q-col-gutter-sm">
                <div class="col-6">
                    <p class="q-mb-xs"><strong>Suburb</strong></p>
                    <q-input
                        :model-value="draft.suburb"
                        outlined :dense="dense" maxlength="255"
                        placeholder="Suburb"
                        @update:model-value="value => updateField('suburb', value)"
                    />
                </div>
                <div class="col-4">
                    <p class="q-mb-xs"><strong>City</strong></p>
                    <q-input
                        :model-value="draft.city"
                        outlined :dense="dense" maxlength="255"
                        placeholder="City"
                        @update:model-value="value => updateField('city', value)"
                    />
                </div>
                <div class="col-2">
                    <p class="q-mb-xs"><strong>Postcode</strong></p>
                    <q-input
                        :model-value="draft.postcode"
                        outlined :dense="dense" maxlength="20"
                        placeholder="Postcode"
                        @update:model-value="value => updateField('postcode', value)"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { GENDER_OPTIONS, ETHNICITY_OPTIONS, IWI_OPTIONS } from "../../../../../shared/demographics";

const ALIAS_SOURCE_OPTIONS = [
    "Preferred Name",
    "Previous Name",
    "Legal Name",
    "Birth Certificate",
    "Driver's License",
    "Passport",
    "NHI / Health Record",
    "Other",
];

const PHONE_TYPE_OPTIONS = ["Mobile", "Home", "Work", "Other"];

/**
 * Presentational, API-free field set for a Te Kohinga (tk_people) record.
 * The parent owns the draft and receives an immutable replacement whenever a
 * field changes.
 * Conversions to/from the API shape (e.g. DD/MM/YYYY <-> ISO date) are the
 * caller's responsibility; this component only edits the draft fields.
 *
 * Used by TkPersonDialog (modal create/edit) and the XLSX resolver (inline,
 * deferred create). `dense` controls input density; `startExpanded` opens the
 * "more options" section by default.
 */
export default {
    name: "TkPersonFields",
    emits: ["update:modelValue"],
    props: {
        // The draft record is owned by the parent.
        modelValue: {
            type: Object,
            required: true,
        },
        dense: {
            type: Boolean,
            default: true,
        },
        startExpanded: {
            type: Boolean,
            default: false,
        },
        // When false, First Name is optional — a blank name is a valid
        // Anonymous (null/null) record. Defaults to true for create/edit.
        requireName: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            showMore: this.startExpanded,
            filteredGenderOptions: [...GENDER_OPTIONS],
            filteredEthnicityOptions: [...ETHNICITY_OPTIONS],
            filteredIwiOptions: [...IWI_OPTIONS],
            filteredAliasSourceOptions: [...ALIAS_SOURCE_OPTIONS],
            filteredPhoneTypeOptions: [...PHONE_TYPE_OPTIONS],
        };
    },
    computed: {
        draft() {
            return this.modelValue;
        },
        phoneNumbers() {
            const stored = Array.isArray(this.modelValue.phone_numbers)
                ? this.modelValue.phone_numbers
                : [];
            if (stored.length) return stored;
            const legacyPhone = String(this.modelValue.phone || "").trim();
            return legacyPhone
                ? [{ phone_type: "Other", phone_number: legacyPhone }]
                : [];
        },
    },
    methods: {
        updateField(field, value) {
            this.$emit("update:modelValue", {
                ...this.modelValue,
                [field]: value,
            });
        },
        addAlias() {
            this.$emit("update:modelValue", {
                ...this.modelValue,
                aliases: [
                    ...(this.modelValue.aliases || []),
                    {
                        first_name: "",
                        last_name: "",
                        source: "",
                    },
                ],
            });
        },
        removeAlias(index) {
            this.$emit("update:modelValue", {
                ...this.modelValue,
                aliases: (this.modelValue.aliases || []).filter((alias, aliasIndex) => aliasIndex !== index),
            });
        },
        updateAliasField(index, field, value) {
            this.$emit("update:modelValue", {
                ...this.modelValue,
                aliases: (this.modelValue.aliases || []).map((alias, aliasIndex) => (
                    aliasIndex === index
                        ? { ...alias, [field]: value }
                        : alias
                )),
            });
        },
        aliasHasName(alias) {
            return !!(String(alias?.first_name ?? "").trim() || String(alias?.last_name ?? "").trim());
        },
        emitPhoneNumbers(phoneNumbers) {
            this.$emit("update:modelValue", {
                ...this.modelValue,
                phone: phoneNumbers[0]?.phone_number || "",
                phone_numbers: phoneNumbers,
            });
        },
        addPhoneNumber() {
            this.emitPhoneNumbers([
                ...this.phoneNumbers,
                {
                    phone_type: this.phoneNumbers.length ? "Other" : "Mobile",
                    phone_number: "",
                },
            ]);
        },
        removePhoneNumber(index) {
            this.emitPhoneNumbers(
                this.phoneNumbers.filter((phone, phoneIndex) => phoneIndex !== index)
            );
        },
        updatePhoneNumberField(index, field, value) {
            this.emitPhoneNumbers(
                this.phoneNumbers.map((phone, phoneIndex) => (
                    phoneIndex === index
                        ? { ...phone, [field]: value }
                        : phone
                ))
            );
        },
        phoneNumberValidation(value) {
            const normalized = String(value ?? "").trim().replace(/[()\s.-]/g, "");
            if (!normalized) return "Phone number is required.";
            return /^(\+?\d+)$/.test(normalized) || "Please enter a valid phone number.";
        },
        filterAliasSources(val, update) {
            update(() => {
                const needle = String(val ?? "").trim().toLowerCase();
                this.filteredAliasSourceOptions = needle
                    ? ALIAS_SOURCE_OPTIONS.filter(option => option.toLowerCase().includes(needle))
                    : [...ALIAS_SOURCE_OPTIONS];
            });
        },
        filterPhoneTypes(val, update) {
            update(() => {
                const needle = String(val ?? "").trim().toLowerCase();
                this.filteredPhoneTypeOptions = needle
                    ? PHONE_TYPE_OPTIONS.filter(option => option.toLowerCase().includes(needle))
                    : [...PHONE_TYPE_OPTIONS];
            });
        },
        seedFilteredOptions() {
            this.filteredGenderOptions = [...GENDER_OPTIONS];
            this.filteredEthnicityOptions = [...ETHNICITY_OPTIONS];
            this.filteredIwiOptions = [...IWI_OPTIONS];
        },
        filterFn(kind, val, update) {
            const sources = { gender: GENDER_OPTIONS, ethnicity: ETHNICITY_OPTIONS, iwi: IWI_OPTIONS };
            const targetKey = `filtered${kind.charAt(0).toUpperCase()}${kind.slice(1)}Options`;
            update(() => {
                const needle = String(val ?? "").toLowerCase();
                this[targetKey] = needle
                    ? sources[kind].filter(opt => String(opt).toLowerCase().includes(needle))
                    : [...sources[kind]];
            });
        },
        // Allow a parent to reset the expand state when reopening.
        collapse() {
            this.showMore = this.startExpanded;
        },
    },
};
</script>
